<template>
  <gs-drawer
    :value="value"
    size="wide"
    :close-label="$t('frontend_cancel')"
    title="Share &amp; embed"
    :subtitle="funnel && funnel.name"
    @input="$emit('input', $event)"
  >
    <div class="gs-share-drawer__segment" role="tablist" aria-label="Share &amp; embed">
      <button
        v-for="tab in tabs"
        :id="`${tabIdPrefix}-${tab.value}-tab`"
        :key="tab.value"
        type="button"
        role="tab"
        :aria-selected="currentTab === tab.value ? 'true' : 'false'"
        :aria-controls="`${tabIdPrefix}-${tab.value}-panel`"
        :class="{ 'is-active': currentTab === tab.value }"
        @click="selectTab(tab.value)"
      >
        {{ tab.title }}
      </button>
    </div>

    <div
      v-if="currentTab === 'embed'"
      :id="`${tabIdPrefix}-embed-panel`"
      class="gs-share-drawer__panel"
      role="tabpanel"
      tabindex="0"
      :aria-labelledby="`${tabIdPrefix}-embed-tab`"
    >
      <p class="gs-share-drawer__intro">
        Drop this widget on your own site so affiliates can sign in and grab their links without leaving your page.
      </p>

      <pre class="gs-share-drawer__code gs-code"><code>{{ embedCode }}</code></pre>

      <div class="gs-share-drawer__actions">
        <gs-button
          @click="copyEmbed()"
          variant="primary"
        >
          <gs-icon name="copy" size="16" />
          Copy code
        </gs-button>
      </div>

      <ol class="gs-share-drawer__steps gs-steps">
        <li>Paste the code into any page on your site.</li>
        <li>The widget sizes itself to the space you give it.</li>
        <li>Affiliates sign in with their {{ whitelabelAppData('sell').title }} account and get their own links.</li>
      </ol>

      <p v-if="!(funnel && funnel.is_live)" class="gs-share-drawer__note">
        The funnel must be published for the widget to load.
      </p>
    </div>

    <div
      v-else
      :id="`${tabIdPrefix}-link-panel`"
      class="gs-share-drawer__panel"
      role="tabpanel"
      tabindex="0"
      :aria-labelledby="`${tabIdPrefix}-link-tab`"
    >
      <p class="gs-share-drawer__intro">
        Send this link to anyone you want promoting the funnel. It opens the affiliate signup and login page.
      </p>

      <div class="gs-share-drawer__copy-card">
        <span class="gs-share-drawer__link">{{ signupLink }}</span>
      </div>

      <div class="gs-share-drawer__actions">
        <gs-button
          @click="copySignupLink()"
          variant="primary"
        >
          <gs-icon name="link" size="16" />
          Copy link
        </gs-button>
        <gs-button variant="outline" @click="openSignupLink()">
          <gs-icon name="external-link" size="16" />
          Open
        </gs-button>
      </div>
    </div>
  </gs-drawer>
</template>

<script>
import Notify from '@groovepages/gd-ui-kit/notify';
import { copyAsset } from '@/components/promotools/copyAsset';

export default {
  name: 'ShareEmbedDrawer',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    funnel: {
      type: Object,
      default: () => ({}),
    },
    permalink: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      tabs: [
        { title: 'Embed code', value: 'embed' },
        { title: 'Affiliate signup link', value: 'link' },
      ],
      currentTab: 'embed',
    };
  },
  computed: {
    tabIdPrefix() {
      return `share-embed-${this._uid}`;
    },
    // Ported verbatim from the retired EmbedWidgetCodeModal so the snippet a
    // vendor already pasted on their site keeps working.
    embedCode() {
      if (this.funnel && this.funnel.slug) {
        const siteAppurl = window.config.appUrl.replace(/^https?:\/\//i, '');
        const sellAppTitle = this.whitelabelAppData('sell').title.toLowerCase();
        return `<div style="width:100%; min-height:220px;">
                  <link href="https://${this.funnel.slug}.${siteAppurl}/promoembed/css/app.css?n=1" rel="stylesheet">
                  <${sellAppTitle}-promo-tools permalink="${this.permalink}"></${sellAppTitle}-promo-tools>
                  <script src="https://${this.funnel.slug}.${siteAppurl}/promoembed/js/app.js?n=1"><\/script>
                </div>`;
      }

      return '';
    },
    // Same formula as ResourcesListView.getFunnelAffiliatePortal.
    signupLink() {
      if (this.funnel && this.funnel.slug) {
        const siteAppurl = window.config.appUrl.replace(/^https?:\/\//i, '');

        return `https://${this.funnel.slug}.${siteAppurl}/${this.funnel.id}/affiliate-signup/${this.permalink}`;
      }

      return '';
    },
  },
  methods: {
    async copyEmbed() {
      try {
        await copyAsset({ text: this.embedCode }, value => this.$copyText(value));
        this.onEmbedCopied();
      } catch (error) {
        this.onEmbedCopyError();
      }
    },
    async copySignupLink() {
      try {
        await copyAsset({ text: this.signupLink }, value => this.$copyText(value));
        this.onSignupLinkCopied();
      } catch (error) {
        this.onSignupLinkCopyError();
      }
    },
    selectTab(newTab) {
      this.currentTab = newTab;
    },
    openSignupLink() {
      window.open(this.signupLink, '_blank');
    },
    onEmbedCopied() {
      Notify.success(this.$t('embed_code_copied'));
    },
    onEmbedCopyError() {
      Notify.fail(this.$t('unable_to_copy_embed_code'));
    },
    onSignupLinkCopied() {
      Notify.success('Affiliate Signup Link Copied To Your Clipboard');
    },
    onSignupLinkCopyError() {
      Notify.fail('Unable To Copy Affiliate Signup Link');
    },
  },
};
</script>

<style scoped>
.gs-share-drawer__segment {
  display: inline-flex;
  gap: 2px;
  padding: 3px;
  background: var(--gs-muted);
  border-radius: var(--gs-radius-md);
}

.gs-share-drawer__segment button {
  min-height: 34px;
  padding: 6px 14px;
  color: var(--gs-muted-foreground);
  background: transparent;
  border: 0;
  border-radius: var(--gs-radius-sm);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color .12s ease, color .12s ease;
}

.gs-share-drawer__segment button:hover {
  color: var(--gs-foreground);
}

.gs-share-drawer__segment button.is-active {
  color: var(--gs-foreground);
  background: var(--gs-surface);
  box-shadow: var(--gs-shadow-sm);
}

.gs-share-drawer__segment button:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--gs-ring);
}

.gs-share-drawer__panel {
  margin-top: 20px;
}

.gs-share-drawer__panel:focus {
  outline: none;
}

.gs-share-drawer__intro {
  margin: 0 0 14px;
  max-width: 62ch;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.55;
}

.gs-share-drawer__code {
  margin: 0;
  padding: 14px 16px;
  overflow-x: auto;
  color: var(--gs-foreground);
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  font-size: 12px;
  line-height: 1.6;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.gs-share-drawer__code code {
  font-family: var(--gs-font-mono);
}

.gs-share-drawer__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
}

.gs-share-drawer__actions ::v-deep .gs-button {
  gap: 8px;
}

.gs-share-drawer__steps {
  margin: 22px 0 0;
  padding-left: 20px;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.6;
}

.gs-share-drawer__steps li + li {
  margin-top: 6px;
}

.gs-share-drawer__note {
  margin: 16px 0 0;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.55;
}

.gs-share-drawer__copy-card {
  padding: 14px 16px;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-share-drawer__link {
  color: var(--gs-foreground);
  font-family: var(--gs-font-mono);
  font-size: 13px;
  line-height: 1.6;
  overflow-wrap: anywhere;
}
</style>
